// จังหวัดในประเทศไทย (docs/PLAN.md เฟส 3: แสดงจังหวัดเมื่ออยู่ในประเทศไทย)
// กริด 1° หยาบเกินกว่าจะแยกจังหวัด จึงเช็กพิกัดกับเส้นเขตจังหวัดจริงจาก Natural Earth (สาธารณสมบัติ)
// ไฟล์ public/geo/th-provinces.json สร้างด้วย npm run build:provinces

import type { LatLon } from "../engine/grid";

/** รูปแบบไฟล์: พิกัดเป็นจำนวนเต็มหน่วย 0.01° เก็บจุดแรกเต็ม จุดถัดไปเก็บเป็นระยะห่างจากจุดก่อนหน้า [lon, lat, dlon, dlat, …] */
export interface ProvinceFile {
  provinces: { code: string; name: string; rings: number[][] }[];
}

export interface Province {
  code: string; // ISO 3166-2 เช่น TH-50
  name: string; // ชื่อไทยไม่มีคำว่า "จังหวัด" เช่น เชียงใหม่
  bbox: [number, number, number, number]; // [ตะวันตก, ใต้, ตะวันออก, เหนือ]
  rings: Float64Array[]; // [lon, lat, lon, lat, …] หน่วยองศา
}

export function parseProvinces(file: ProvinceFile): Province[] {
  return file.provinces.map(({ code, name, rings: packed }) => {
    const bbox: Province["bbox"] = [Infinity, Infinity, -Infinity, -Infinity];
    const rings = packed.map((r) => {
      const out = new Float64Array(r.length);
      let lon = 0;
      let lat = 0;
      for (let i = 0; i < r.length; i += 2) {
        lon += r[i];
        lat += r[i + 1];
        out[i] = lon / 100;
        out[i + 1] = lat / 100;
        bbox[0] = Math.min(bbox[0], out[i]);
        bbox[1] = Math.min(bbox[1], out[i + 1]);
        bbox[2] = Math.max(bbox[2], out[i]);
        bbox[3] = Math.max(bbox[3], out[i + 1]);
      }
      return out;
    });
    return { code, name, bbox, rings };
  });
}

/** จุดอยู่ในจังหวัดนี้ไหม (นับจำนวนครั้งที่ตัดเส้นขอบ วงในของรูพรุนก็นับถูกเอง) */
function contains(p: Province, { lat, lon }: LatLon): boolean {
  if (lon < p.bbox[0] || lon > p.bbox[2] || lat < p.bbox[1] || lat > p.bbox[3]) return false;
  let inside = false;
  for (const r of p.rings) {
    for (let i = 0, j = r.length - 2; i < r.length; j = i, i += 2) {
      const [xi, yi, xj, yj] = [r[i], r[i + 1], r[j], r[j + 1]];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

/** ระยะจากจุดถึงขอบจังหวัดโดยประมาณ (องศา) วัดถึงจุดยอดของเส้นขอบ */
function distance(p: Province, { lat, lon }: LatLon): number {
  let best = Infinity;
  const k = Math.cos((lat * Math.PI) / 180);
  for (const r of p.rings) {
    for (let i = 0; i < r.length; i += 2) best = Math.min(best, Math.hypot((r[i] - lon) * k, r[i + 1] - lat));
  }
  return best;
}

/**
 * จังหวัดที่จุดนี้อยู่ ถ้าไม่อยู่ในจังหวัดไหน (เช่น ชายฝั่งที่เส้นขอบลดจุดไปแล้ว หรืออยู่ในทะเล)
 * ใช้จังหวัดที่ใกล้ที่สุดภายในระยะ maxDeg
 */
export function provinceAt(provinces: readonly Province[], at: LatLon, maxDeg = 0.3): Province | null {
  for (const p of provinces) if (contains(p, at)) return p;
  let best: Province | null = null;
  let bestD = maxDeg;
  for (const p of provinces) {
    if (lon0(p, at) > maxDeg + 0.5) continue;
    const d = distance(p, at);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

// กรองจังหวัดที่กรอบอยู่ไกลเกินไปก่อนวัดระยะจริง
function lon0(p: Province, { lat, lon }: LatLon): number {
  const dx = lon < p.bbox[0] ? p.bbox[0] - lon : lon > p.bbox[2] ? lon - p.bbox[2] : 0;
  const dy = lat < p.bbox[1] ? p.bbox[1] - lat : lat > p.bbox[3] ? lat - p.bbox[3] : 0;
  return Math.max(dx, dy);
}

/** ชื่อเต็ม เช่น "จังหวัดเชียงใหม่" กรุงเทพฯ ไม่ใช่จังหวัดจึงเรียกชื่อตรงๆ */
export const provinceLabel = (p: Pick<Province, "code" | "name">) => (p.code === "TH-10" ? p.name : `จังหวัด${p.name}`);
